import React from 'react';
import { Typography } from '@mui/material';
import { TableFooterSectionProps } from '../../types/footer-types';
import { useTableFooter } from '../../hooks/use-table-footer';
import {
  StyledFooterSection,
  StyledTotalContainer,
  StyledSummaryContainer,
  StyledSummaryItem,
  StyledStickyFooter,
} from '../styled/table-footer-styles';

/**
 * Table Footer Section Component
 *
 * A standalone footer component that can be positioned independently of the table structure.
 * This is useful when you want to show summary information outside the table or when
 * the table doesn't have pagination and you want a separate footer section.
 *
 * ## Features
 *
 * - **Flexible Positioning**: Can be used anywhere in your layout
 * - **Responsive Design**: Automatically stacks on mobile devices
 * - **Custom Styling**: Supports custom CSS through className prop
 * - **Sticky Option**: Can be made sticky for better UX
 * - **Theme Integration**: Consistent with table styling
 *
 * ## Layout
 *
 * - **Left Side**: Total rows count with customizable label
 * - **Right Side**: Summary values in a horizontal list
 * - **Mobile**: Stacks vertically for better readability
 *
 * @example
 * ```tsx
 * <TableFooterSection
 *   rows={data}
 *   config={{
 *     enabled: true,
 *     totalRowsLabel: "Total Products",
 *     summaryColumns: [
 *       {
 *         columnId: 'price',
 *         label: 'Total Value',
 *         callback: (rows, values, helpers) =>
 *           `$${helpers.formatNumber(helpers.sum(values))}`
 *       },
 *       {
 *         columnId: 'quantity',
 *         label: 'Avg Quantity',
 *         callback: (rows, values, helpers) =>
 *           helpers.formatNumber(helpers.average(values), { maximumFractionDigits: 1 })
 *       }
 *     ],
 *     sticky: true
 *   }}
 * />
 * ```
 */
export function TableFooterSection<T extends Record<string, unknown>>(
  props: TableFooterSectionProps<T>,
): React.ReactElement | null {
  const { rows, config, className } = props;

  const { summaryValues, totalRowsCount, totalRowsLabel } = useTableFooter({
    rows,
    config,
  });

  // Don't render if footer is disabled or no configuration
  if (
    !config.enabled ||
    (!config.summaryColumns.length && !config.totalRowsLabel)
  ) {
    return null;
  }

  const footerContent = (
    <StyledFooterSection className={className} sx={config.sx}>
      {/* Left side - Total rows count */}
      <StyledTotalContainer>
        <Typography variant="body2" component="span" className="total-label">
          {totalRowsLabel}:
        </Typography>
        <Typography variant="body2" component="span" className="total-value">
          {totalRowsCount}
        </Typography>
      </StyledTotalContainer>

      {/* Right side - Summary values */}
      {summaryValues.length > 0 && (
        <StyledSummaryContainer>
          {summaryValues.map((summary, index) => (
            <StyledSummaryItem key={`${String(summary.columnId)}-${index}`}>
              <Typography
                variant="body2"
                component="span"
                className="summary-label"
              >
                {summary.label}:
              </Typography>
              <Typography
                variant="body2"
                component="span"
                className="summary-value"
              >
                {summary.value}
              </Typography>
            </StyledSummaryItem>
          ))}
        </StyledSummaryContainer>
      )}
    </StyledFooterSection>
  );

  // Wrap in sticky container if enabled
  if (config.sticky) {
    return <StyledStickyFooter>{footerContent}</StyledStickyFooter>;
  }

  return footerContent;
}

TableFooterSection.displayName = 'TableFooterSection';
